import {useEffect, useRef, useState} from "react";
import {Ban, Gift, KeyRound, Pencil, ShieldAlert} from "lucide-react";
import {useQueryClient} from "@tanstack/react-query";
import {ConfirmationDialog} from "../../shared/components/ConfirmationDialog";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {
    changeUserRefCode,
    setUserFraud,
    setUserRefEnabled,
    setUserStatus,
} from "./api";
import {mapUserApiError} from "./errors";
import {UserSupportCodeDialog} from "./UserSupportCodeDialog";
import "../../shared/finance/financeOperations.css";
import {useTranslation} from "../../shared/i18n";


const responseMessage = (response, fallback) =>
    typeof response?.message === "string" ? response.message : fallback;

export const UserProfileActions = ({
    userId,
    user,
    role,
    canManage,
    canManageFlags = true,
    countryId,
    onUserUpdated,
}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const refPendingRef = useRef(false);
    const flagPendingRef = useRef(false);
    const [refOpen, setRefOpen] = useState(false);
    const [refCode, setRefCode] = useState("");
    const [refPending, setRefPending] = useState(false);
    const [refError, setRefError] = useState(null);
    const [supportCodeOpen, setSupportCodeOpen] = useState(false);
    // 403 «цель не клиент» — прячем кнопку до конца просмотра этой карточки.
    const [supportCodeForbidden, setSupportCodeForbidden] = useState(false);
    const [flagAction, setFlagAction] = useState(null);
    const [flagPending, setFlagPending] = useState(false);
    const [flagError, setFlagError] = useState(null);
    const [result, setResult] = useState("");

    useEffect(() => {
        setRefOpen(false);
        setFlagAction(null);
        setSupportCodeOpen(false);
        setSupportCodeForbidden(false);
        setResult("");
        refPendingRef.current = false;
        flagPendingRef.current = false;
    }, [userId]);

    if (!canManage) return null;

    const entity = user?.username || user?.phone_number || t("sections.users.p14", {v0: userId});
    const superadmin = role === ADMIN_ROLES.SUPERADMIN && canManageFlags;

    const openRef = () => {
        setRefCode(String(user?.ref_code || ""));
        setRefError(null);
        setRefOpen(true);
    };

    const submitRef = async ({ref_code: submittedRefCode}) => {
        if (refPendingRef.current) return;
        const normalized = submittedRefCode.trim();
        if (!/^[A-Z0-9]+$/.test(normalized)) {
            setRefError({message: t("sections.users.p15")});
            return;
        }
        refPendingRef.current = true;
        setRefPending(true);
        setRefError(null);
        try {
            await changeUserRefCode({userId, refCode: normalized, countryId});
            onUserUpdated({ref_code: normalized});
            setResult(t("sections.users.p16"));
            setRefOpen(false);
        } catch (error) {
            setRefError(mapUserApiError(error));
        } finally {
            refPendingRef.current = false;
            setRefPending(false);
        }
    };

    const flagDefinitions = {
        status: {
            title: user?.is_blocked ? t("sections.users.p17") : t("sections.users.p18"),
            confirmLabel: user?.is_blocked ? t("sections.users.p19") : t("sections.users.p20"),
            target: user?.is_blocked ? t("sections.users.p21") : t("sections.users.p22"),
            run: () => setUserStatus({userId, isBlocked: !user?.is_blocked}),
            patch: {is_blocked: !user?.is_blocked},
            icon: Ban,
        },
        referral: {
            title: user?.block_ref ? t("sections.users.p23") : t("sections.users.p24"),
            confirmLabel: user?.block_ref ? t("sections.users.p25") : t("sections.users.p26"),
            target: user?.block_ref ? t("sections.users.p27") : t("sections.users.p28"),
            run: () => setUserRefEnabled({userId, isRefEnabled: Boolean(user?.block_ref)}),
            patch: {block_ref: !user?.block_ref},
            icon: Gift,
        },
        fraud: {
            title: user?.is_fraud ? t("sections.users.p29") : t("sections.users.p30"),
            confirmLabel: user?.is_fraud ? t("sections.users.p31") : t("sections.users.p32"),
            target: user?.is_fraud ? t("sections.users.p33") : t("sections.users.p34"),
            run: () => setUserFraud({userId, isFraud: !user?.is_fraud}),
            patch: {is_fraud: !user?.is_fraud},
            icon: ShieldAlert,
        },
    };

    const submitFlag = async () => {
        if (!flagAction || flagPendingRef.current) return;
        const definition = flagDefinitions[flagAction];
        flagPendingRef.current = true;
        setFlagPending(true);
        setFlagError(null);
        try {
            const response = await definition.run();
            onUserUpdated(response?.user_data || definition.patch);
            setResult(responseMessage(response, t("sections.users.p35")));
            setFlagAction(null);
        } catch (error) {
            setFlagError(mapUserApiError(error));
        } finally {
            flagPendingRef.current = false;
            setFlagPending(false);
        }
    };

    const selectedFlag = flagAction ? flagDefinitions[flagAction] : null;

    return (
        <section className="admin-card" aria-labelledby="user-profile-actions-title">
            <p className="admin-card-title" id="user-profile-actions-title">{t("sections.users.p36")}</p>
            {result ? <div className="admin-card-note" role="status">{result}</div> : null}
            <div className="admin-inline-actions">
                <button type="button" className="admin-button admin-button-secondary" onClick={openRef}>
                    <Pencil aria-hidden="true" size={15} /> {t("sections.users.p37")}
                </button>
                {!supportCodeForbidden ? (
                    <button type="button" className="admin-button admin-button-secondary" onClick={() => setSupportCodeOpen(true)}>
                        <KeyRound aria-hidden="true" size={15} /> {t("sections.users.p38")}
                    </button>
                ) : null}
                {superadmin ? Object.entries(flagDefinitions).map(([key, definition]) => {
                    const Icon = definition.icon;
                    return (
                        <button key={key} type="button" className="admin-button admin-button-secondary" onClick={() => {
                            setFlagError(null);
                            setFlagAction(key);
                        }}>
                            <Icon aria-hidden="true" size={15} /> {definition.title}
                        </button>
                    );
                }) : null}
            </div>

            <FinancialCommandDialog
                open={refOpen}
                dialogKey={`${userId}:ref-code`}
                title={t("sections.users.p37")}
                entity={entity}
                fields={[{name: "ref_code", label: t("sections.users.p39"), value: refCode, required: true}]}
                confirmLabel={t("sections.users.p40")}
                pending={refPending}
                error={refError}
                onCancel={() => {
                    if (!refPendingRef.current) setRefOpen(false);
                }}
                onConfirm={submitRef}
            />

            <UserSupportCodeDialog
                open={supportCodeOpen}
                userId={userId}
                entity={entity}
                onCancel={() => setSupportCodeOpen(false)}
                onForbidden={() => setSupportCodeForbidden(true)}
                onNotFound={() => queryClient.invalidateQueries({queryKey: ["admin-user-detail", userId]})}
            />

            <ConfirmationDialog
                open={Boolean(selectedFlag)}
                dialogKey={`${userId}:${flagAction || ""}`}
                title={selectedFlag?.title || ""}
                entity={entity}
                description={selectedFlag ? t("sections.users.p41", {v0: selectedFlag.target}) : ""}
                confirmLabel={selectedFlag?.confirmLabel}
                tone={flagAction === "status" || flagAction === "fraud" ? "destructive" : "primary"}
                pending={flagPending}
                error={flagError}
                onCancel={() => {
                    if (!flagPendingRef.current) setFlagAction(null);
                }}
                onConfirm={submitFlag}
            />
        </section>
    );
};
